import { z } from 'zod';

// Deprecate this when there is a good way to extract prisma types
// TODO: Rename to persisted schema
export const FormType = z.union([
  z.literal('PRIVACY_POLICY'),
  z.literal('CONTACT_ACKNOWLEDGEMENT'),
  z.literal('PREQUAL'),
  z.literal('HARD_CREDIT_APPLICATION'),
  z.literal('HARD_CREDIT_APPLICATION_MODIFICATION'),
  z.literal('IDENTITY_THEFT_PREVENTION'),
  z.literal('IDENTITY_VERIFICATION'),
  z.literal('TEST_DRIVE_DISCLOSURE'),
  z.literal('DMV_DOCUMENTS'),
  z.literal('CUSTOM')
]);

// TODO: Rename to persisted schema
export type FormType = z.TypeOf<typeof FormType>;

export const FinanceType = z.union([
  z.literal('CASH'),
  z.literal('LEASE'),
  z.literal('FINANCE'),
  z.literal('OUTSIDE_FINANCING'),
  z.literal('UNDECIDED')
]);
export type FinanceType = z.TypeOf<typeof FinanceType>;

export const IdCardType = z.union([
  z.literal('DRIVERS_LICENSE'),
  z.literal('STATE_ID'),
  z.literal('PASSPORT'),
  z.literal('OTHER')
]);
export type IdCardType = z.TypeOf<typeof IdCardType>;

// Base dashboard user role
const BaseUserRoleSchema = z.union([
  z.literal('ADMIN'),
  z.literal('FNI_MANAGER'),
  z.literal('BDC'),
  z.literal('SALES_MANAGER'),
  z.literal('SALES_PERSON')
]);

// User roles is all role includes the customer
export const UserRole = z.union([BaseUserRoleSchema, z.literal('CUSTOMER')]);
export type UserRole = z.TypeOf<typeof UserRole>;

// This mirrors the Prisma `enum Permission {`
export const ExtraPermissionPersistedSchema = z.union([
  z.literal('OVERRIDE_TASKS'),
  // @deprecated Use OVERRIDE_TASKS instead
  // TODO: Should be removed after 21 Jan release
  z.literal('OVERRIDE_ID_CHECKS')
]);
export type ExtraPermissionPersistedSchema = z.TypeOf<
  typeof ExtraPermissionPersistedSchema
>;

// Dashboard user roles only include dashboard roles
export const DashboardUserRoleSchema = BaseUserRoleSchema;
export type DashboardUserRole = z.TypeOf<typeof DashboardUserRoleSchema>;

export const IdDocumentInfo = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  middleName: z.string().optional(),
  suffix: z.string().optional(),
  cityInAddress: z.string().optional(),
  zipCodeInAddress: z.string().optional(),
  stateInAddress: z.string().optional(),
  stateName: z.string().optional(),
  documentNumber: z.string().optional(),
  expirationDate: z.string().optional(),
  dateOfBirth: z.string().optional(),
  dateOfIssue: z.string().optional(),
  idType: z.string().optional(),
  endorsements: z.string().optional(),
  veteran: z.string().optional(),
  restrictions: z.string().optional(),
  class: z.string().optional(),
  address: z.string().optional(),
  county: z.string().optional(),
  placeOfBirth: z.string().optional(),
  mrzCode: z.string().optional()
});
export type IdDocumentInfo = z.TypeOf<typeof IdDocumentInfo>;

export const AddressHousingStatusPersistedSchema = z.union([
  z.literal('OWNING'),
  z.literal('RENTING'),
  z.literal('LIVING_WITH_RELATIVES'),
  z.literal('OTHER')
]);
export type AddressHousingStatusPersistedSchema = z.infer<
  typeof AddressHousingStatusPersistedSchema
>;

export const EmploymentStatus = z.union([
  z.literal('EMPLOYED'),
  z.literal('SELF_EMPLOYED'),
  z.literal('STUDENT'),
  z.literal('RETIRED'),
  z.literal('UNEMPLOYED')
]);
export type EmploymentStatus = z.TypeOf<typeof EmploymentStatus>;

export const IncomeInterval = z.union([
  z.literal('YEARLY'),
  z.literal('MONTHLY'),
  z.literal('WEEKLY'),

  // Deprecated
  z.literal('BI_WEEKLY')
]);
export type IncomeInterval = z.TypeOf<typeof IncomeInterval>;

export const TransactionStatusSchema = z.union([
  z.literal('REGISTERED'),
  z.literal('SIGNED_UP'),
  z.literal('IN_PROGRESS'),

  z.literal('PREQUAL_SUBMITTED'),
  z.literal('CREDIT_APP_SUBMITTED'),
  z.literal('FILE_COMPLETE'),
  z.literal('READY_FOR_FNI'),

  z.literal('DELIVERED'),

  // Deprecate
  z.literal('WEB_LEAD'), // For web prequal applications
  z.literal('WEB_APPLICATION'), // For web hard credit applications
  z.literal('COMPLETED'),
  z.literal('DISASSOCIATED'),
  z.literal('DEAD'),
  z.literal('RULE_90_DAYS'),
  z.literal('NOT_STARTED')
]);
export type TransactionStatusSchema = z.infer<typeof TransactionStatusSchema>;

export const TransactionSourcePersistedSchema = z.union([
  z.literal('KIOSK'),
  z.literal('ADMIN_DASHBOARD'),
  z.literal('WEB_PREQUAL'),
  z.literal('WEB_APPLICATION'),
  z.literal('CUSTOMER_REFERRAL')
]);
export type TransactionSourcePersistedSchema = z.infer<
  typeof TransactionSourcePersistedSchema
>;

export const VehicleBodyTypeSchema = z.union([
  z.literal('SEDAN'),
  z.literal('SUV'),
  z.literal('COUPE'),
  z.literal('MINIVAN'),
  z.literal('OTHER')
]);
export type VehicleBodyTypeSchema = z.TypeOf<typeof VehicleBodyTypeSchema>;

export const CdkDealJacketStatusPersistedSchema = z.union([
  z.literal('STAGED_FOR_PUSHING'),
  z.literal('PUSHED'),
  z.literal('FAILED')
]);
export type CdkDealJacketStatusPersistedSchema = z.infer<
  typeof CdkDealJacketStatusPersistedSchema
>;

export const VehicleRegistrationAddressAnswer = z.union([
  z.literal('SAME'),
  z.literal('DIFFERENT')
]);

export type VehicleRegistrationAddressAnswer = z.infer<
  typeof VehicleRegistrationAddressAnswer
>;

// Used for persisted event logs
export const FormSubmissionAccessPersistedSchema = z.union([
  z.literal('DEALERSHIP'),
  z.literal('CUSTOMER'),
  z.literal('BOTH')
]);

export type FormSubmissionAccessPersistedSchema = z.infer<
  typeof FormSubmissionAccessPersistedSchema
>;

/**
 * As per 29 Oct, formFieldId added as possible key
 *
 * Core difference of prefillPath and formFieldId as key:
 *
 * - Prefill Path   : Used for form field that prefilled by system (e.g: Transaction Comment, Vehicle Make)
 * - Form Field Id  : Used for form field that should be filled by user (e.g: SIGNATURE)
 */
export const FormSubmissionCollectionSnapshotPersistedSchema = z.record(
  z.string(),
  z.object({
    original: z.string().optional(),

    // Use undefined if there is no valid signers, should not use [] it can be interpreted wrongly
    // Undefined clearly defines that the value is expected to be undefined
    // When it is empty array it can be interpreted that nobody assigned tos sign this
    validSigners: z.array(z.string()).optional(),
    isStale: z.boolean()
  })
);

export type FormSubmissionCollectionSnapshotPersistedSchema = z.infer<
  typeof FormSubmissionCollectionSnapshotPersistedSchema
>;

// This mirrors the Prisma `enum DocumentLabelCode {`
export const DocumentLabelCodePersistedSchema = z.union([
  z.literal('INSURANCE_BINDER'),
  z.literal('INSURANCE_CARD'),
  z.literal('ID_CARD'),
  z.literal('TRADE_REGISTRATION'),
  z.literal('VEHICLE_FINANCE_LEASE_AGREEMENT'),
  z.literal('BUYERS_ORDER'),
  z.literal('BILL_OF_SALE'),
  z.literal('OFAC_CLEARANCE')
]);
export type DocumentLabelCodePersistedSchema = z.infer<
  typeof DocumentLabelCodePersistedSchema
>;

